import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Container, Alert, Button } from '@mui/material';

// Where to send the user back to once they're signed in. The whole OAuth
// round trip is a real (non-SPA) navigation away from and back to the app —
// `window.location.href` below, then the backend's own redirects — so a deep
// link like `/anomalies?anomaly=<id>` would otherwise be lost, landing the
// user on the default list instead of what they clicked. Stashed here right
// before leaving, restored by App.js once `isAuthenticated` flips true.
export const POST_LOGIN_REDIRECT_KEY = 'postLoginRedirect';

function stashReturnPath() {
  const path = window.location.pathname + window.location.search;
  if (path === '/') return; // nothing to restore — the default landing page
  try { sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, path); } catch { /* private browsing, etc. */ }
}

function LoginPage() {
  const [error, setError] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authError = params.get('auth_error');
    if (authError) {
      const messages = {
        access_denied: 'Access was denied. Please try again.',
        domain_not_allowed: 'Only @commerceiq.ai accounts are allowed.',
        account_deactivated: 'Your account has been deactivated. Contact an administrator.',
        token_exchange_failed: 'Authentication failed. Please try again.',
        userinfo_failed: 'Could not retrieve your profile. Please try again.',
        no_email: 'No email found in your Google account.',
        missing_code: 'Authentication flow was interrupted. Please try again.',
        no_access_token: 'Authentication failed. Please try again.',
      };
      setError(messages[authError] || 'Authentication failed. Please try again.');
      window.history.replaceState({}, '', '/');
    } else {
      // No error — redirect directly to Google OAuth. The stashed path (if
      // any) survives this and the earlier attempt's own stash, so retrying
      // after an error still returns to the originally-requested page.
      stashReturnPath();
      window.location.href = '/auth/google/login';
    }
  }, []);

  // No error means we're redirecting — render nothing
  if (!error) {
    return null;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={24}
          sx={{
            p: 6,
            borderRadius: 4,
            textAlign: 'center',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2,
              }}
            >
              Anomaly Feedback System
            </Typography>
          </Box>

          <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
            {error}
          </Alert>

          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={() => { stashReturnPath(); window.location.href = '/auth/google/login'; }}
            startIcon={
              <Box
                component="img"
                src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                alt=""
                sx={{ width: 20, height: 20 }}
              />
            }
            sx={{
              py: 1.5,
              px: 4,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '1.1rem',
              fontWeight: 600,
              backgroundColor: '#fff',
              color: '#3c4043',
              border: '1px solid #dadce0',
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              '&:hover': {
                backgroundColor: '#f7f8f8',
                boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
                border: '1px solid #d2d2d2',
              },
              transition: 'all 0.2s ease',
            }}
          >
            Try Again with Google
          </Button>

          <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid #e0e0e0' }}>
            <Typography variant="body2" color="text.secondary">
              Only @commerceiq.ai accounts are allowed. <br />
              Contact your administrator for access.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default LoginPage;
